import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  SubjectOfferingService,
  TopicMaterialService,
  MediaService,
  MaterialAssetService
} from '../../../services';

@Component({
  selector: 'app-content-publisher',
  standalone: false,
  templateUrl: './content-publisher.html',
  styleUrl: './content-publisher.css',
})
export class ContentPublisher implements OnInit {

  loading = false;
  errorMsg = '';
  

  allOfferings: any[] = [];
  filteredOfferings: any[] = [];
  pagedOfferings: any[] = [];
  searchQuery = '';
  page = 0;
  size = 9;

  selectedOffering: any = null;
  selectedTopic: any = null;

  topics: any[] = [];
  topicsLoading = false;
  topicsError = '';

  materials: any[] = [];
  materialsLoading = false;
  materialsError = '';

  showPublishModal = false;
  publishStep = 1;

  publishForm: any = {
    title: '',
    description: '',
    materialType: 'PDF',
    publish: true,
    requestProcessing: false,
  };

  selectedFile: File | null = null;
  filePreviewUrl: string | null = null;
  uploadProgress: 'idle' | 'uploading' | 'done' | 'error' = 'idle';
  uploadedAssetId: number | null = null;
  uploadedAssetUrl: string | null = null;
  createdMaterialId: number | null = null;

  publishLoading = false;
  publishError = '';
  publishSuccess = false;

  showDeleteMaterialModal = false;
  selectedMaterial: any = null;
  deleteMaterialLoading = false;
  deleteMaterialError = '';

  materialTypes = [
    { key: 'PDF', label: 'PDF', icon: 'file-earmark-pdf', accept: 'application/pdf', color: '#ef4444' },
    { key: 'VIDEO', label: 'Video', icon: 'play-btn', accept: 'video/mp4,video/webm', color: '#8b5cf6' },
    { key: 'IMAGE', label: 'Image', icon: 'image', accept: 'image/jpeg,image/png,image/webp', color: '#10b981' },
    { key: 'NOTE', label: 'Note', icon: 'sticky', accept: '', color: '#f59e0b' },
    { key: 'LINK', label: 'Link', icon: 'link-45deg', accept: '', color: '#3b82f6' },
  ];

  constructor(
    private offeringService: SubjectOfferingService,
    private topicMaterialService: TopicMaterialService,
    private mediaService: MediaService,
    private materialAssetService: MaterialAssetService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchOfferings();
  }

  fetchOfferings() {
    this.loading = true;
    this.errorMsg = '';
    this.cdr.detectChanges();
    this.offeringService.getAll(0, 1000).subscribe({
      next: (res: any) => {
        this.allOfferings = res?.data?.content ?? res?.data ?? [];
        this.applyFilter();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.errorMsg = err?.error?.message || 'Failed to load subject offerings.';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  applyFilter() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredOfferings = q
      ? this.allOfferings.filter(o =>
          o.subjectName?.toLowerCase().includes(q) ||
          o.formName?.toLowerCase().includes(q) ||
          o.academicYear?.toLowerCase().includes(q)
        )
      : [...this.allOfferings];
    this.page = 0;
    this.updatePage();
  }

  updatePage() {
    const start = this.page * this.size;
    this.pagedOfferings = this.filteredOfferings.slice(start, start + this.size);
  }

  get totalPages(): number { return Math.ceil(this.filteredOfferings.length / this.size); }
  get hasNext(): boolean { return this.page < this.totalPages - 1; }
  get hasPrev(): boolean { return this.page > 0; }

  nextPage() { if (!this.hasNext) return; this.page++; this.updatePage(); }
  prevPage() { if (!this.hasPrev) return; this.page--; this.updatePage(); }
  onSearch() { this.applyFilter(); }
  getMinValue(a: number, b: number) { return Math.min(a, b); }

  selectOffering(offering: any) {
    this.selectedOffering = offering;
    this.selectedTopic = null;
    this.topics = [];
    this.materials = [];
    this.topicsError = '';
    this.fetchTopics(offering.id);
  }

  backToOfferings() {
    this.selectedOffering = null;
    this.selectedTopic = null;
    this.topics = [];
    this.materials = [];
  }

  fetchTopics(offeringId: number) {
    this.topicsLoading = true;
    this.topicsError = '';
    this.cdr.detectChanges();
    this.offeringService.getTopics(offeringId).subscribe({
      next: (res: any) => {
        this.topics = res?.data ?? [];
        this.topicsLoading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.topicsError = err?.error?.message || 'Failed to load topics.';
        this.topicsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  selectTopic(topic: any) {
    this.selectedTopic = topic;
    this.materials = [];
    this.materialsError = '';
    this.fetchMaterials(topic.id);
     this.setDefaultTitle();
  }

  setDefaultTitle() {
  const topicTitle = this.selectedTopic?.title || '';

  const typeSuffixMap: any = {
    PDF: 'Notes',
    VIDEO: 'Video',
    IMAGE: 'Diagram',
    NOTE: 'Summary',
    LINK: 'Resource Link'
  };

  const suffix = typeSuffixMap[this.publishForm.materialType] || 'Material';

  this.publishForm.title = `${topicTitle} - ${suffix}`;
}

  backToTopics() {
    this.selectedTopic = null;
    this.materials = [];
  }

  fetchMaterials(topicId: number) {
    this.materialsLoading = true;
    this.materialsError = '';
    this.cdr.detectChanges();
    this.topicMaterialService.getTopicMaterials(topicId).subscribe({
      next: (res: any) => {
        this.materials = res?.data ?? [];
        this.materialsLoading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.materialsError = err?.error?.message || 'Failed to load materials.';
        this.materialsLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  fetchMaterialsSilently(topicId: number) {
    this.topicMaterialService.getTopicMaterials(topicId).subscribe({
      next: (res: any) => {
        this.materials = res?.data ?? [];
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  get selectedMaterialType(): any {
    return this.materialTypes.find(t => t.key === this.publishForm.materialType);
  }

  get acceptedFileTypes(): string {
    return this.selectedMaterialType?.accept ?? '';
  }

  get fileUploadAllowed(): boolean {
    return ['PDF', 'VIDEO', 'IMAGE'].includes(this.publishForm.materialType);
  }

  openPublishModal() {
    this.publishForm = {
      title: '',
      description: '',
      materialType: 'PDF',
      publish: true,
      requestProcessing: false,
    };
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.uploadProgress = 'idle';
    this.uploadedAssetId = null;
    this.uploadedAssetUrl = null;
    this.createdMaterialId = null;
    this.publishError = '';
    this.publishSuccess = false;
    this.publishStep = 1;
    this.showPublishModal = true;
     this.setDefaultTitle();
  }

  closePublishModal() {
    if (this.publishLoading) return;
    this.showPublishModal = false;
    this.publishError = '';
    this.publishSuccess = false;
  }

  onTypeChange(type: string) {
    this.publishForm.materialType = type;
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.uploadProgress = 'idle';
    this.uploadedAssetId = null;
    this.uploadedAssetUrl = null;

     this.setDefaultTitle();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.selectedFile = input.files[0];
    this.filePreviewUrl = null;
    this.uploadProgress = 'idle';
    this.uploadedAssetId = null;

    if (this.selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreviewUrl = e.target?.result as string;
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(this.selectedFile);
    }
  }

  removeFile() {
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.uploadProgress = 'idle';
    this.uploadedAssetId = null;
    this.uploadedAssetUrl = null;
  }

  goToStep2() {
    if (!this.publishForm.title.trim()) {
      this.publishError = 'Please enter a title for this material.';
      return;
    }
    this.publishError = '';
    this.publishStep = 2;
  }

  goToStep1() {
    this.publishStep = 1;
    this.publishError = '';
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getMaterialTypeIcon(type: string): string {
    return this.materialTypes.find(t => t.key === type)?.icon ?? 'file-earmark';
  }

  getMaterialTypeColor(type: string): string {
    return this.materialTypes.find(t => t.key === type)?.color ?? '#6b7280';
  }

  async submitPublish() {
    if (!this.publishForm.title.trim()) {
      this.publishError = 'Please enter a title for this material.';
      return;
    }

    this.publishLoading = true;
    this.publishError = '';
    this.cdr.detectChanges();

    try {
      let mediaAssetIds: number[] = [];

      if (this.selectedFile && this.fileUploadAllowed) {
        this.uploadProgress = 'uploading';
        this.cdr.detectChanges();

        const uploadRes: any = await this.mediaService.uploadFile(this.selectedFile).toPromise();
        this.uploadedAssetId = uploadRes?.data?.mediaAssetId;
        this.uploadedAssetUrl = uploadRes?.data?.url;
        this.uploadProgress = 'done';

        if (this.uploadedAssetId) {
          mediaAssetIds = [this.uploadedAssetId];
        }
        this.cdr.detectChanges();
      }

      const materialPayload = {
        title: this.publishForm.title.trim(),
        description: this.publishForm.description?.trim() || undefined,
        materialType: this.publishForm.materialType,
        publish: this.publishForm.publish,
        mediaAssetIds,
        requestProcessing: this.publishForm.requestProcessing,
      };

      const materialRes: any = await this.topicMaterialService
        .createTopicMaterial(this.selectedTopic.id, materialPayload)
        .toPromise();

      this.createdMaterialId = materialRes?.data?.id;

      // if (this.createdMaterialId && this.uploadedAssetId) {
      //   await this.materialAssetService.linkAssetToMaterial(this.createdMaterialId, {
      //     mediaAssetId: this.uploadedAssetId,
      //     sortOrder: 1,
      //     role: 'PRIMARY',
      //   }).toPromise();
      // }

      this.publishLoading = false;
      this.publishSuccess = true;
      this.publishStep = 3;
      this.fetchMaterialsSilently(this.selectedTopic.id);
      this.cdr.detectChanges();

    } catch (err: any) {
      this.uploadProgress = 'error';
      this.publishLoading = false;
      this.publishError = err?.error?.message || 'Failed to publish content. Please try again.';
      this.cdr.detectChanges();
    }
  }

  openDeleteMaterialModal(material: any) {
    this.selectedMaterial = material;
    this.deleteMaterialError = '';
    this.showDeleteMaterialModal = true;
  }

  closeDeleteMaterialModal() {
    if (this.deleteMaterialLoading) return;
    this.showDeleteMaterialModal = false;
    this.deleteMaterialError = '';
    this.selectedMaterial = null;
  }

  confirmDeleteMaterial() {
    if (!this.selectedMaterial || !this.selectedTopic) return;
    this.deleteMaterialLoading = true;
    this.deleteMaterialError = '';
    this.topicMaterialService.deleteTopicMaterial(this.selectedTopic.id, this.selectedMaterial.id).subscribe({
      next: () => {
        this.deleteMaterialLoading = false;
        this.showDeleteMaterialModal = false;
        this.selectedMaterial = null;
        this.fetchMaterialsSilently(this.selectedTopic.id);
      },
      error: (err: any) => {
        this.deleteMaterialLoading = false;
        this.deleteMaterialError = err?.error?.message || 'Failed to delete material.';
        this.cdr.detectChanges();
      }
    });
  }
}